import React, { useMemo, useState, useEffect } from 'react';
import { useSeedEffect, type SeedKey } from '../../hooks/useSeedEffect';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import SchemaForm from '../../schemaForm/SchemaForm';
import CoverImageField from '../../schemaForm/fields/CoverImageField';
import BankAccountField from '../../schemaForm/fields/BankAccountField';
import MultiSelect, { MultiSelectOption, SelectId } from '../MultiSelect';
import type { SchemaFieldProps, SchemaFormSection } from '../../schemaForm/types';
import '../../App.css';

interface ISelectOption {
  _id: string | number;
  name: string;
}

export interface IActivityFormData {
  name: string;
  description: string;
  visibility: 'public' | 'internal' | 'hidden';
  joinPolicy: 'open' | 'approval';
  sortOrder: number;
  coverImage: string;
  /** null = collect into the organization's default account. */
  bankAccountId: number | null;
  groupIds: SelectId[];
  templateIds: SelectId[];
}

interface IActivityFormProps {
  initialData?: Partial<IActivityFormData>;
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  onSubmit: (data: IActivityFormData) => Promise<void>;
  loading: boolean;
  submitButtonText: string;
  /** Optional pre-fetched groups to avoid redundant API calls */
  prefetchedGroups?: ISelectOption[];
  /** Optional pre-fetched templates to avoid redundant API calls */
  prefetchedTemplates?: ISelectOption[];
  /**
   * When provided (edit flow only), enables the "Upload image" button next
   * to the cover image URL input. The create flow lacks an ID, so the button
   * is hidden there; users can still paste a URL.
   */
  activityId?: number;
}

/**
 * Thin wrapper around the schema-driven SchemaForm. Preserves the external
 * props the consuming pages (ActivityCreatePage quick form, ActivityEditPage)
 * rely on and the payload contract (name/description/visibility/joinPolicy/
 * sortOrder/coverImage/groupIds/templateIds), while delegating field rendering
 * to the activity entity meta.
 *
 * coverImage is a `widget: 'custom'` meta field rendered via the extracted
 * CoverImageField override (the upload button needs the activityId, bound here).
 * bankAccountId is likewise a `widget: 'custom'` meta field: null means "use
 * the organization default account", which the generic fk widget cannot express.
 *
 * groupIds/templateIds are not part of the activity entity meta (route-managed
 * assignment data), so they stay as inline MultiSelect nodes to keep the payload
 * contract unchanged.
 */
const ActivityForm: React.FC<IActivityFormProps> = ({
  initialData,
  seedKey,
  onSubmit,
  loading,
  submitButtonText,
  prefetchedGroups,
  prefetchedTemplates,
  activityId,
}) => {
  const { t } = useTranslation();
  const [groupIds, setGroupIds] = useState<SelectId[]>([]);
  const [templateIds, setTemplateIds] = useState<SelectId[]>([]);
  const [allGroups, setAllGroups] = useState<ISelectOption[]>([]);
  const [allTemplates, setAllTemplates] = useState<ISelectOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const hasPrefetchedOptions = prefetchedGroups && prefetchedTemplates;

  // Fetch groups and event templates for the assignment MultiSelects.
  useEffect(() => {
    if (hasPrefetchedOptions) {
      setAllGroups(prefetchedGroups);
      setAllTemplates(prefetchedTemplates);
      setOptionsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [groupsRes, templatesRes] = await Promise.all([
          api.groups.list(),
          api.eventTemplates.list(),
        ]);
        if (cancelled) return;
        setAllGroups(groupsRes.data as ISelectOption[]);
        setAllTemplates(templatesRes.data as ISelectOption[]);
      } catch {
        // Non-fatal: assignment selects just stay empty.
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPrefetchedOptions, prefetchedGroups, prefetchedTemplates]);

  // Seed the assignment ids from initialData (the SchemaForm fields seed
  // separately from initialValues below).
  useSeedEffect(initialData, seedKey, (seed) => {
    setGroupIds(seed?.groupIds ?? []);
    setTemplateIds(seed?.templateIds ?? []);
  });

  const initialValues = useMemo(
    () => ({
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      visibility: initialData?.visibility ?? 'hidden',
      joinPolicy: initialData?.joinPolicy ?? 'approval',
      sortOrder: initialData?.sortOrder ?? 0,
      coverImage: initialData?.coverImage ?? '',
      bankAccountId: initialData?.bankAccountId ?? null,
    }),
    [initialData]
  );

  // Bind activityId into the cover-image override (enables the upload button on
  // the edit flow). SchemaForm passes the standard SchemaFieldProps through.
  const BoundCoverImageField = useMemo(() => {
    const Bound: React.FC<SchemaFieldProps> = (props) => (
      <CoverImageField {...props} activityId={activityId} />
    );
    return Bound;
  }, [activityId]);

  // joinPolicy only applies to non-hidden activities (legacy behaviour).
  const visibility = useMemo(
    () => ({ joinPolicy: (values: Record<string, any>) => values.visibility !== 'hidden' }),
    []
  );

  // Reuse the existing heterogeneous enum i18n keys instead of the generated
  // `activitys.enum.*` fallback.
  const enumLabelKeys = useMemo(
    () => ({
      visibility: {
        hidden: 'activities.visibilityHidden',
        internal: 'activities.visibilityInternal',
        public: 'activities.visibilityPublic',
      },
      joinPolicy: {
        approval: 'activities.joinPolicyApproval',
        open: 'activities.joinPolicyOpen',
      },
    }),
    []
  );

  const groupsField = (
    <MultiSelect
      key="activity-groups"
      id="groupIds"
      label={t('activities.groups')}
      labelHelpText={t('activities.groupsHelp')}
      options={allGroups as MultiSelectOption[]}
      selectedIds={groupIds}
      onChange={setGroupIds}
      placeholder={t('activities.groups')}
    />
  );

  const templatesField = (
    <MultiSelect
      key="activity-templates"
      id="templateIds"
      label={t('activities.templates')}
      labelHelpText={t('activities.templatesHelp')}
      options={allTemplates as MultiSelectOption[]}
      selectedIds={templateIds}
      onChange={setTemplateIds}
      placeholder={t('activities.templates')}
    />
  );

  const sections: SchemaFormSection[] = useMemo(
    () => [
      {
        items: ['name', 'description', 'visibility', 'joinPolicy', 'sortOrder', 'coverImage', 'bankAccountId', groupsField, templatesField],
      },
    ],
    // groupsField/templatesField re-create when their backing state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupsField, templatesField]
  );

  const handleSubmit = (values: Record<string, any>) => {
    return onSubmit({
      name: values.name,
      description: values.description ?? '',
      visibility: values.visibility,
      joinPolicy: values.joinPolicy,
      sortOrder: Number(values.sortOrder) || 0,
      coverImage: values.coverImage ?? '',
      bankAccountId: values.bankAccountId ?? null,
      groupIds,
      templateIds,
    });
  };

  if (optionsLoading) {
    return <p>{t('common.loading')}</p>;
  }

  return (
    <SchemaForm
      entity="activity"
      sections={sections}
      overrides={{ coverImage: BoundCoverImageField, bankAccountId: BankAccountField }}
      visibility={visibility}
      enumLabelKeys={enumLabelKeys}
      initialValues={initialValues}
      seedKey={seedKey}
      onSubmit={handleSubmit}
      submitLabel={submitButtonText}
      loading={loading}
    />
  );
};

export default ActivityForm;
